import { Grid, Tab, Tabs } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import useRequest from 'hooks/useRequest';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import routes from 'store/configs/Routes';
import { useHistory, useLocation } from 'react-router';
import PartnerDashBoardPageView from 'components/partner/PartnerDashBoardPageView';
import { DonationCampaign } from 'store/types/DonationFund';
import PartnerService from 'services/api/PartnerServices';
import PartnerCampaignDetailsPageView from 'components/partner/PartnerCampaignDetailsPageView';
import ApplicationsService from 'services/api/ApplicationsService';
import Application from 'store/types/Application';
import Spinner from 'components/shared/Spinner';
import { useSnackbar } from 'notistack';
import { ParametersContext } from 'components/ParametersGuard';
import { ConfigContext } from 'components/ConfigGuard';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';

const tabConfig = {
  Partner: { key: 'Partner', label: 'Partner DashBoard', route: routes.partner } as TabItem,
  PartnerDetails: {
    key: 'PartnerDetails',
    label: 'Campaign Details',
    route: routes.partnerDetails,
  } as TabItem,
};

const PartnerPageView: React.FunctionComponent = () => {
  const { error, loading, data = [], refetch } = useRequest<DonationCampaign[]>(PartnerService.getPartners);
  const history = useHistory();
  const { replace, push } = useHistory();
  const [submitLoading, setSubmitLoading] = useState<boolean>();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const { pathname } = useLocation();
  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);
  const {
    partner: {
      fundReconciliationReportApplicationId,
      fundRequestApplicationId,
      partnerDashboardTabHeader,
      campaignDetailsTabHeader,
    },
  } = useContext(ParametersContext);
  const {
    modulesSettings: {
      partner: { hiddenTabs },
    },
  } = useContext(ConfigContext);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.Partner.key) => {
      replace(getTabKeyByRoute(value, tabConfig));
    },
    [replace]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, visibleTabs]);

  const handleSelectedCampaignChange = useCallback(
    (campaignId: string) => {
      setSelectedCampaignId(campaignId);
      replace(getTabRouteByKey(tabConfig.PartnerDetails.key, tabConfig));
    },
    [replace]
  );

  const handleCreateApplication = useCallback(
    (applicationId: string) => {
      setSubmitLoading(true);
      ApplicationsService.createApplication(applicationId, selectedCampaignId)
        .then(({ id }: Application) => {
          setSubmitLoading(false);
          enqueueSnackbar('Draft application successfully created', { variant: 'success' });
          push(`${routes.editApplication}/${id}`);
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [enqueueSnackbar, push, selectedCampaignId]
  );

  return (
    <Spinner loading={submitLoading} fullPage={true}>
      {error ? (
        <Alert severity={'error'} className={commonStyles.alert}>
          {error}
        </Alert>
      ) : (
        <Grid {...defaultGridContainerProps} justifyContent={'space-between'}>
          <Grid {...defaultGridItemProps} xs={true}>
            <TabContext value={activeTab}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor={'primary'}
                textColor={'primary'}
                variant={'scrollable'}
              >
                {visibleTabs.map((tab) => {
                  return (
                    <Tab
                      key={tab.key}
                      value={tab.key}
                      label={tab.label}
                      className={commonStyles.tab}
                      classes={{ selected: commonStyles.activeTab }}
                    />
                  );
                })}
              </Tabs>
              <TabPanel value={tabConfig.Partner.key} className={commonStyles.tabPanel}>
                {partnerDashboardTabHeader && <p dangerouslySetInnerHTML={{ __html: partnerDashboardTabHeader }} />}
                <PartnerDashBoardPageView
                  loading={loading}
                  onSelectedCampaignIdChange={handleSelectedCampaignChange}
                  campaigns={data}
                  onCreateApplicationSubmit={handleCreateApplication}
                  fundReconciliationReportApplicationId={fundReconciliationReportApplicationId}
                  refetchCampaigns={refetch}
                  fundRequestApplicationId={fundRequestApplicationId}
                />
              </TabPanel>
              <TabPanel value={tabConfig.PartnerDetails.key} className={commonStyles.tabPanel}>
                {campaignDetailsTabHeader && <p dangerouslySetInnerHTML={{ __html: campaignDetailsTabHeader }} />}
                <PartnerCampaignDetailsPageView
                  onSelectedCampaignIdChange={handleSelectedCampaignChange}
                  selectedCampaignId={selectedCampaignId}
                  loading={loading}
                  campaigns={data}
                  onCreateApplicationSubmit={handleCreateApplication}
                  applicationId={fundRequestApplicationId}
                />
              </TabPanel>
            </TabContext>
          </Grid>
        </Grid>
      )}
    </Spinner>
  );
};

export default PartnerPageView;
